'use client'

/**
 * Rail Watch — live tape under Pulse.
 * Free: 1 slot. Operator+: 5. Edit pins listed names. Never invent prices.
 * Scan lives on the rail (Symbol / Last / Chg). Trade pane no longer duplicates it.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Popover, Tooltip } from 'antd'
import { LockOutlined, SearchOutlined } from '@ant-design/icons'
import { usePathname, useRouter } from 'next/navigation'
import { usePlanEntitlements } from '@/hooks/use-plan-entitlements'
import { useListedSymbolSearch, useTickerList } from '@/domains/market-data/hooks/useMarketData'
import { useOptionalClientDeskSession } from '@/shared/providers/context/ClientDeskSessionContext'
import { useAuth } from '@/domains/auth'
import { isTradingDeskSidebarPath, previewWatchTier } from '@/shared/utils/sidebar/previewSidebar'
import { finiteDeskNumber, finiteTapePrint, formatTapePrice, positionMarkPrice } from '@/shared/components/widgets/lib/deskFormat'
import {
  defaultWatchSelection,
  expandWatchSearchQuery,
  hasWatchStorage,
  matchesWatchQuery,
  normalizeWatchSelection,
  normalizeWatchSymbol,
  readWatchSelection,
  railWatchLabel,
  watchDisplayName,
  watchSlotCount,
  writeWatchSelection,
} from './watchCatalog'
import styles from './Sidebar.module.css'

const TRADE_HREF = '/trading'
const MARKETS_HREF = '/market-data'
const PRODUCT_NAME = 'Watch'
const PRODUCT_TAG = 'Live tape'
const TAPE_HIT_CAP = 8
const RAIL_WATCH_VISIBLE = 5

export type RailWatchStripProps = {
  collapsed: boolean
}

function symbolFromPosition(row: Record<string, unknown>): string | null {
  return normalizeWatchSymbol(row.symbol ?? row.instrument ?? row.ticker)
}

function positionDayPct(row: Record<string, unknown>): number | null {
  return finiteDeskNumber(row.day_change_percent ?? row.dayChangePercent)
}

function formatLast(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  if (abs > 0 && abs < 0.001) {
    return value.toExponential(2)
  }
  return formatTapePrice(value)
}

function formatChg(value: number | null): string {
  if (value == null) return '—'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(2)}%`
}

function WatchMark({ live }: { live?: boolean }) {
  return (
    <span
      className={`${styles.pulseMark} ${live ? styles.pulseMarkLive : ''}`}
      aria-hidden
    >
      <span className={styles.pulseMarkCore} />
      <span className={styles.pulseMarkRing} />
    </span>
  )
}

function formatVenue(raw: string): string {
  const venue = raw.trim()
  if (!venue || venue.toLowerCase() === 'tape') return 'Tape'
  return venue.charAt(0).toUpperCase() + venue.slice(1).toLowerCase()
}

function lookupPrint(
  symbol: string,
  quotes: Array<{
    symbol?: string
    price?: number
    change_percent?: number
    bid?: number
    ask?: number
  }> | undefined,
  quotesReady: boolean,
  held?: { mark: number | null; chg: number | null }
) {
  const quote = quotesReady
    ? (quotes || []).find((row) => String(row.symbol || '').toUpperCase() === symbol)
    : undefined
  const last = finiteTapePrint(quote?.price) ?? finiteTapePrint(held?.mark) ?? null
  const chg = finiteDeskNumber(quote?.change_percent) ?? held?.chg ?? null
  const bid = finiteTapePrint(quote?.bid) ?? null
  const ask = finiteTapePrint(quote?.ask) ?? null
  const live = finiteTapePrint(quote?.price) != null
  return { last, chg, bid, ask, live, markOnly: !live && last != null }
}

export function RailWatchStrip({ collapsed }: RailWatchStripProps) {
  const router = useRouter()
  const pathname = usePathname()
  const pinsOnly = false
  const onTradeDesk = isTradingDeskSidebarPath(pathname)
  const productName = onTradeDesk ? 'Watchlist' : PRODUCT_NAME
  const desk = useOptionalClientDeskSession()
  const { isAuthenticated } = useAuth()
  const plan = usePlanEntitlements()
  const planLoading = plan.isLoading
  const tier = previewWatchTier(pathname, isAuthenticated, plan.tier)
  const isFree = tier === plan.tier ? plan.isFree : false
  const [mounted, setMounted] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [pickerOpen, setPickerOpen] = useState(false)
  const [searchDraft, setSearchDraft] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const posReady = desk?.workspace?.sections?.positions?.status === 'ready'

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQ(searchDraft.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchDraft])

  useEffect(() => {
    if (!pickerOpen) {
      setSearchDraft('')
      setSearchQ('')
      return
    }
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [pickerOpen])

  const bookBySymbol = useMemo(() => {
    const map = new Map<string, { mark: number | null; chg: number | null }>()
    if (!posReady) return map
    for (const row of desk?.workspace?.positions || []) {
      const symbol = symbolFromPosition(row)
      if (!symbol || map.has(symbol)) continue
      map.set(symbol, {
        mark: positionMarkPrice(row),
        chg: positionDayPct(row),
      })
    }
    return map
  }, [desk?.workspace?.positions, posReady])

  const bookSymbols = useMemo(() => Array.from(bookBySymbol.keys()), [bookBySymbol])

  useEffect(() => {
    if (!mounted || planLoading) return
    if (hasWatchStorage()) {
      setSelected(readWatchSelection(tier))
      return
    }
    if (!posReady) return
    setSelected(defaultWatchSelection(tier, bookSymbols))
  }, [bookSymbols, mounted, planLoading, posReady, tier])

  const slots = watchSlotCount(tier)
  const showPaidTeaser = (isFree || tier === 'none') && slots === 1

  const persist = useCallback(
    (next: string[]) => {
      const normalized = normalizeWatchSelection(next, tier)
      setSelected(normalized)
      writeWatchSelection(normalized)
    },
    [tier]
  )

  const toggleSymbol = useCallback(
    (symbol: string, display?: string) => {
      if (display) {
        setLabels((prev) => (prev[symbol] === display ? prev : { ...prev, [symbol]: display }))
      }
      if (selected.includes(symbol)) {
        if (selected.length <= 1) return
        persist(selected.filter((x) => x !== symbol))
        return
      }
      if (slots <= 1 && selected.length >= 1) {
        persist([symbol])
        return
      }
      if (selected.length >= slots) {
        persist([...selected.slice(1), symbol])
        return
      }
      persist([...selected, symbol])
    },
    [persist, selected, slots]
  )

  const listedQuery = searchQ.trim().length >= 2 ? expandWatchSearchQuery(searchQ) : searchQ
  const listedSearch = useListedSymbolSearch(listedQuery)
  const tapeHits = listedSearch.data?.symbols ?? []
  const tapeVenue = formatVenue(listedSearch.data?.exchange || 'tape')
  const tapePreview = useMemo(() => {
    if (!pickerOpen) return []
    const seen = new Set<string>()
    const next: string[] = []
    for (const hit of tapeHits) {
      const symbol = normalizeWatchSymbol(hit.symbol)
      if (!symbol || seen.has(symbol)) continue
      seen.add(symbol)
      next.push(symbol)
      if (next.length >= TAPE_HIT_CAP) break
    }
    return next
  }, [pickerOpen, tapeHits])

  const quoteSymbols = useMemo(() => {
    if (pinsOnly && !pickerOpen) return []
    const next = [...selected]
    if (!pickerOpen) return next
    for (const symbol of bookSymbols) {
      if (!next.includes(symbol)) next.push(symbol)
    }
    for (const symbol of tapePreview) {
      if (!next.includes(symbol)) next.push(symbol)
    }
    return next
  }, [bookSymbols, pickerOpen, pinsOnly, selected, tapePreview])

  const { data, isError } = useTickerList(quoteSymbols)
  const lastQuotes = useRef(data)
  if (data) lastQuotes.current = data
  const quotes = data ?? lastQuotes.current
  const quotesReady = Boolean(quotes) && !isError

  useEffect(() => {
    if (tapeHits.length === 0) return
    setLabels((prev) => {
      const next = { ...prev }
      let changed = false
      for (const hit of tapeHits) {
        const symbol = normalizeWatchSymbol(hit.symbol)
        if (!symbol) continue
        const display = watchDisplayName(
          symbol,
          typeof hit.display === 'string' ? hit.display.trim() : ''
        )
        if (!display || next[symbol] === display) continue
        next[symbol] = display
        changed = true
      }
      return changed ? next : prev
    })
  }, [tapeHits])

  const rows = useMemo(() => {
    return selected.map((symbol) => {
      const print = lookupPrint(symbol, quotes, quotesReady, bookBySymbol.get(symbol))
      return {
        symbol,
        label: railWatchLabel(symbol, labels[symbol]),
        ...print,
      }
    })
  }, [bookBySymbol, labels, quotes, quotesReady, selected])

  const hasLive = rows.some((row) => row.live)
  const hasMark = rows.some((row) => row.markOnly)
  const showChg = rows.some((row) => row.chg != null)
  const source = hasLive ? 'Last' : hasMark ? 'Mark' : 'Last'
  const sourceTitle = hasLive
    ? 'Last print from the tape'
    : hasMark
      ? 'Mark from the open book'
      : 'Waiting for a print'

  const pickerGroups = useMemo(() => {
    const bookSet = new Set(bookSymbols)
    const tapeItems = tapeHits
      .map((hit) => {
        const symbol = normalizeWatchSymbol(hit.symbol)
        if (!symbol || bookSet.has(symbol)) return null
        return { symbol, display: watchDisplayName(symbol, hit.display?.trim() || symbol) }
      })
      .filter((row): row is { symbol: string; display: string } => Boolean(row))
      .slice(0, TAPE_HIT_CAP)
    const tapeSet = new Set(tapeItems.map((row) => row.symbol))
    const other = selected
      .filter((symbol) => !bookSet.has(symbol) && !tapeSet.has(symbol))
      .map((symbol) => ({ symbol, display: watchDisplayName(symbol, labels[symbol]) }))
    const searching = searchQ.trim().length >= 2
    const book = {
      id: 'book',
      title: 'Book',
      hint: null as string | null,
      items: bookSymbols
        .map((symbol) => ({ symbol, display: watchDisplayName(symbol, labels[symbol]) }))
        .filter((item) => matchesWatchQuery(item.symbol, item.display, searchQ)),
    }
    const tape = {
      id: 'tape',
      title: 'Tape',
      hint: tapeVenue,
      items: tapeItems,
    }
    const held = {
      id: 'held',
      title: 'Pinned',
      hint: null as string | null,
      items: other.filter((item) => matchesWatchQuery(item.symbol, item.display, searchQ)),
    }
    return (searching ? [tape, book, held] : [book, held]).filter(
      (group) => group.items.length > 0
    )
  }, [bookSymbols, labels, searchQ, selected, tapeHits, tapeVenue])

  const searchStatus = useMemo(() => {
    if (searchQ.length > 0 && searchQ.length < 2) return 'Two letters to search.'
    if (listedSearch.isFetching) return 'Searching…'
    if (listedSearch.isError) return 'Tape search failed.'
    if (searchQ.length >= 2 && tapeHits.length === 0 && pickerGroups.length === 0) {
      return `No listed name for ${searchQ.toUpperCase()}.`
    }
    return null
  }, [listedSearch.isError, listedSearch.isFetching, pickerGroups.length, searchQ, tapeHits.length])

  const picker = useMemo(
    () => (
      <div className={styles.watchPicker} role="menu" aria-label="Watch names">
        <header className={styles.watchPickerHead}>
          <div className={styles.watchPickerBrand}>
            <WatchMark live={hasLive} />
            <div className={styles.watchPickerBrandText}>
              <p className={styles.watchPickerName}>{PRODUCT_NAME}</p>
              <p className={styles.watchPickerTag}>{PRODUCT_TAG}</p>
            </div>
          </div>
          <span className={styles.pulseSlots} title={`${selected.length} of ${slots} watch slots`}>
            {selected.length}/{slots}
          </span>
        </header>
        <div className={styles.watchPickerSearch}>
          <label className={styles.watchSearchSr} htmlFor="rail-watch-search">
            Search listed names
          </label>
          <div
            className={styles.watchSearchWell}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                event.preventDefault()
                searchRef.current?.focus()
              }
            }}
          >
            <SearchOutlined className={styles.watchSearchMark} aria-hidden />
            <input
              id="rail-watch-search"
              ref={searchRef}
              className={styles.watchSearch}
              type="text"
              autoComplete="off"
              autoFocus
              spellCheck={false}
              placeholder="Search listed names"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
            {searchDraft ? (
              <button
                type="button"
                className={styles.watchSearchClear}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setSearchDraft('')
                  setSearchQ('')
                  searchRef.current?.focus()
                }}
              >
                Clear
              </button>
            ) : null}
          </div>
          {searchStatus ? <p className={styles.watchPickerStatus}>{searchStatus}</p> : null}
        </div>
        <div className={styles.watchPickerBody}>
          {pickerGroups.length === 0 ? (
            searchStatus ? null : (
              <p className={`${styles.watchPickerStatus} ${styles.watchPickerPad}`}>
                Search the venue to pin a name.
              </p>
            )
          ) : (
            <>
            {pickerGroups.map((group) => (
              <section key={group.id} className={styles.watchPickerSection}>
                <p className={styles.watchPickerBand}>
                  <span className={styles.watchPickerBandTitle}>{group.title}</span>
                  <span className={styles.watchPickerBandMeta}>
                    {group.id === 'tape'
                      ? tapeHits.length > TAPE_HIT_CAP
                        ? `${group.hint} · ${TAPE_HIT_CAP}/${tapeHits.length}`
                        : `${group.hint} · ${group.items.length}`
                      : group.items.length}
                  </span>
                </p>
                {group.items.length === 0 ? (
                  <p className={styles.watchPickerStatus}>No listed names yet.</p>
                ) : (
                  <ul className={styles.watchPickerList}>
                    {group.items.map((item) => {
                      const active = selected.includes(item.symbol)
                      const print = lookupPrint(
                        item.symbol,
                        quotes,
                        quotesReady
                      )
                      const up = print.chg != null && print.chg > 0
                      const down = print.chg != null && print.chg < 0
                      return (
                        <li key={`${group.id}-${item.symbol}`}>
                          <button
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={active}
                            className={`${styles.watchPickRow} ${
                              active ? styles.watchPickRowOn : ''
                            }`}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              toggleSymbol(item.symbol, item.display)
                              window.requestAnimationFrame(() => searchRef.current?.focus())
                            }}
                          >
                            <span
                              className={`${styles.watchPickPip} ${
                                active ? styles.watchPickPipOn : ''
                              }`}
                              aria-hidden
                            />
                            <span className={styles.watchPickSym}>{item.display}</span>
                            <span
                              className={`${styles.watchPickLast} ${
                                print.last == null ? styles.watchPickLastEmpty : ''
                              }`}
                            >
                              {formatLast(print.last)}
                              {print.chg != null ? (
                                <span
                                  className={`${styles.watchPickChg} ${
                                    up ? styles.watchUp : down ? styles.watchDown : ''
                                  }`}
                                >
                                  {formatChg(print.chg)}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </section>
            ))}
            </>
          )}
        </div>
        {showPaidTeaser ? (
          <p className={styles.watchPickerFoot}>
            Unlock 5 on <strong>Operator+</strong>
          </p>
        ) : null}
      </div>
    ),
    [
      bookBySymbol,
      quotes,
      hasLive,
      pickerGroups,
      quotesReady,
      tapeHits.length,
      searchDraft,
      searchStatus,
      selected,
      showPaidTeaser,
      slots,
      toggleSymbol,
    ]
  )

  const openSymbol = (symbol?: string) => {
    if (symbol) desk?.setSymbol(symbol)
    if (pathname === MARKETS_HREF) return
    if (pathname === TRADE_HREF) {
      if (!symbol) return
      router.push(`${TRADE_HREF}?symbol=${encodeURIComponent(symbol)}`)
      return
    }
    router.push(
      symbol
        ? `${MARKETS_HREF}?symbol=${encodeURIComponent(symbol)}`
        : MARKETS_HREF
    )
  }

  if (collapsed) {
    return (
      <aside
        className={`${styles.pulse} ${styles.pulseCollapsed} ${styles.watchStrip}`}
        aria-label={productName}
      >
        <Tooltip title={`${productName} — ${PRODUCT_TAG}`} placement="right">
          <button
            type="button"
            className={styles.pulseCollapsedBtn}
            aria-label={`${productName} — open markets`}
            onClick={() => openSymbol()}
          >
            <WatchMark live={hasLive} />
          </button>
        </Tooltip>
      </aside>
    )
  }

  const productTag = pinsOnly ? 'Pin source' : PRODUCT_TAG

  return (
    <aside
      className={`${styles.pulse} ${styles.watchStrip} ${
        pinsOnly ? styles.watchStripPinsOnly : ''
      }`}
      data-scan={onTradeDesk ? 'full' : undefined}
      aria-label={productName}
    >
      <header className={styles.pulseHeader}>
        <div className={styles.pulseBrand}>
          <WatchMark live={pinsOnly ? false : hasLive} />
          <div className={styles.pulseBrandText}>
            <h2 className={styles.pulseTitle} title={`${productName} · ${productTag}`}>
              {productName}
            </h2>
          </div>
        </div>
        <div className={styles.pulseHeaderActions}>
          <span
            className={styles.pulseSlots}
            title={`${selected.length} of ${slots} watch slots`}
          >
            {selected.length}/{slots}
          </span>
          <Popover
            content={picker}
            trigger="click"
            placement="rightTop"
            arrow={false}
            align={{ offset: [10, -6] }}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            classNames={{ root: 'sidebar-watch-popover' }}
          >
            <button
              type="button"
              className={styles.pulseEdit}
              aria-label={`Customize ${productName}`}
              aria-expanded={pickerOpen}
            >
              ···
            </button>
          </Popover>
        </div>
      </header>

      {pinsOnly ? (
        <p className={styles.watchEmpty}>
          {selected.length === 0
            ? 'Edit to pin a listed name.'
            : 'Pins live on Trade.'}
        </p>
      ) : (
      <>
      <div
        className={`${styles.watchCols} ${showChg ? styles.watchColsChg : ''}`}
        aria-hidden
      >
        <span />
        <span>Sym</span>
        <span>Last</span>
        {showChg ? <span>Chg</span> : null}
        {onTradeDesk ? <span>Bid</span> : null}
        {onTradeDesk ? <span>Ask</span> : null}
      </div>

      <div className={styles.watchStack}>
        {rows.length === 0 ? (
          <p className={styles.watchEmpty}>Edit to pin a listed name.</p>
        ) : (
          rows.slice(0, RAIL_WATCH_VISIBLE).map((row) => {
            const up = row.chg != null && row.chg > 0
            const down = row.chg != null && row.chg < 0
            const rowSource = row.live ? 'Last' : row.markOnly ? 'Mark' : source
            return (
              <button
                key={row.symbol}
                type="button"
                className={`${styles.watchRow} ${showChg ? styles.watchRowChg : ''} ${
                  desk?.symbol === row.symbol ? styles.watchRowOn : ''
                }`}
                aria-label={`${row.label} ${rowSource.toLowerCase()} ${formatLast(row.last)} bid ${formatLast(row.bid)} ask ${formatLast(row.ask)}`}
                aria-current={desk?.symbol === row.symbol ? 'true' : undefined}
                data-status={row.live ? 'Live' : row.last != null ? 'Delayed' : 'Unavailable'}
                title={
                  row.live
                    ? `Live · last print · select ${row.symbol}`
                    : row.markOnly
                      ? `Delayed · book mark · select ${row.symbol}`
                      : `Unavailable · ${sourceTitle} · select ${row.symbol}`
                }
                onClick={() => openSymbol(row.symbol)}
              >
                <span
                  className={`${styles.watchPip} ${
                    row.live ? styles.watchPipLive : row.markOnly ? styles.watchPipMark : ''
                  }`}
                  aria-hidden
                />
                <span className={styles.watchSym}>{row.label}</span>
                <span className={styles.watchLast}>{formatLast(row.last)}</span>
                {showChg ? (
                  <span
                    className={`${styles.watchChg} ${
                      up ? styles.watchUp : down ? styles.watchDown : ''
                    }`}
                  >
                    {formatChg(row.chg)}
                  </span>
                ) : null}
                {onTradeDesk ? (
                  <span className={styles.watchBid}>{formatLast(row.bid)}</span>
                ) : null}
                {onTradeDesk ? (
                  <span className={styles.watchAsk}>{formatLast(row.ask)}</span>
                ) : null}
              </button>
            )
          })
        )}
        {showPaidTeaser ? (
          <button
            type="button"
            className={`${styles.widgetCard} ${styles.widgetLocked}`}
            onClick={() => router.push('/settings')}
            aria-label="More names locked — Operator+ required"
          >
            <div className={styles.widgetBand}>
              <span className={styles.widgetBandLabel}>More names</span>
              <LockOutlined className={styles.widgetLockIcon} aria-hidden />
            </div>
            <div className={styles.widgetBody}>
              <p className={styles.widgetLockCopy}>
                Unlock with <strong>Operator+</strong>
              </p>
            </div>
          </button>
        ) : null}
      </div>
      </>
      )}
    </aside>
  )
}

export default RailWatchStrip
